import {
    setDraftFloatField,
    setDraftIntegerField,
    setDraftStringField,
} from '../../../../../shared/utils/draftFieldParsers';

import { formatPurchaseOrderDateForInput, formatPurchaseOrderDateTimeForInput } from './purchaseOrderDateUtils';
import { DELIVERY_TYPE_PICKUP, normalizeToUpperCase } from './purchaseOrderDisplayUtils';

export const DEFAULT_DRAFT = {
    vendorId: null,
    deliveryType: '',
    isReceivedAtCrossDock: false,
    pickUpDate: '',
    originalRequestedDeliveryDate: '',
    scheduledDeliveryDate: '',
    scheduledTime: '00:00',
    shipperId: '',
    crossDockId: '',
    crossDockDeliveryDate: '',
    facilities: [],
    buyers: [],
    minTempReq: '',
    maxTempReq: '',
    estimatedFreightCost: '',
    specialInstructions: '',
    priority: false,
};

export const STORAGE_TEMP_ZONE_RANGES = {
    'Ambient (>40)': { min: 40, max: 60 },
    'Ambient (>55)': { min: 55, max: 60 },
    'Chilled (32-35)': { min: 32, max: 35 },
    'Frozen (0-31)': { min: 0, max: 31 },
    'Nightshade (50-55)': { min: 50, max: 55 },
    'Refrigerated (36-40)': { min: 36, max: 40 },
};

export function getTempRangeForStorageZone(storageTempZone) {
    if (!storageTempZone) return null;
    return STORAGE_TEMP_ZONE_RANGES[String(storageTempZone)] ?? null;
}

/**
 * Builds the editable primary draft shape for a purchase order.
 * This is intentionally "edit-only" normalization (input-friendly strings, ids, etc.).
 *
 * @param {Object|null|undefined} purchaseOrder
 * @returns {Object}
 */
export function buildPurchaseOrderPrimaryDraft(purchaseOrder) {
    return {
        deliveryType: normalizeToUpperCase(purchaseOrder?.deliveryType),
        pickupDate: formatPurchaseOrderDateForInput(purchaseOrder?.pickupDate),
        scheduledDeliveryDate: formatPurchaseOrderDateTimeForInput(purchaseOrder?.scheduledDeliveryDate),
        vendorId: purchaseOrder?.vendorID ?? purchaseOrder?.vendorId ?? null,
        shipperId: purchaseOrder?.shipperID ?? '',
        crossDockId: purchaseOrder?.crossDockID ?? '',
        crossDockDeliveryDate: formatPurchaseOrderDateForInput(purchaseOrder?.crossDockDeliveryDate),
        facilityId: purchaseOrder?.facilityID,
        buyers: Array.from(
            new Set(purchaseOrder?.buyers?.map((buyer) => buyer.authUserID).filter((id) => id != null) ?? []),
        ),
        minTempReq: purchaseOrder?.minTempReq ?? '',
        maxTempReq: purchaseOrder?.maxTempReq ?? '',
        specialInstructions: purchaseOrder?.specialInstructions ?? '',
        estimatedFreightCost: purchaseOrder?.estimatedFreightCost ?? '',
        priority: purchaseOrder?.priority === true,
        reasonCode: purchaseOrder?.reasonCode ?? '',
    };
}

export function buildDuplicatePrimaryDraft(sourcePO) {
    if (!sourcePO) return { ...DEFAULT_DRAFT };

    return {
        vendorId: sourcePO?.vendorID ?? sourcePO?.vendorId ?? null,
        deliveryType: normalizeToUpperCase(sourcePO?.deliveryType) || '',
        crossDockId: sourcePO?.crossDockID ?? sourcePO?.crossDockId ?? '',
        isReceivedAtCrossDock: sourcePO?.isReceivedAtCrossDock === true,
        minTempReq: sourcePO?.minTempReq ?? '',
        maxTempReq: sourcePO?.maxTempReq ?? '',
        shipperId: sourcePO?.shipperID ?? sourcePO?.shipperId ?? '',
        buyers: Array.from(
            new Set(sourcePO?.buyers?.map((buyer) => buyer.authUserID ?? buyer.id).filter((id) => id != null) ?? []),
        ),
        priority: sourcePO?.priority === true,
        facilities: [],
        pickUpDate: '',
        originalRequestedDeliveryDate: '',
        scheduledDeliveryDate: '',
        scheduledTime: '00:00',
        crossDockDeliveryDate: '',
        specialInstructions: '',
        estimatedFreightCost: '',
    };
}

export function buildDuplicateItems(sourcePODetail) {
    const items = sourcePODetail?.items ?? sourcePODetail?.purchaseOrderItems ?? [];
    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item, index) => {
        const rawUnitPrice = item?.unitPrice ?? item?.casePrice;
        const numericUnitPrice =
            rawUnitPrice === null || rawUnitPrice === undefined || rawUnitPrice === '' ? null : Number(rawUnitPrice);
        const unitPrice = Number.isFinite(numericUnitPrice) ? numericUnitPrice.toFixed(2) : '';

        return {
            id: `dup-${Date.now()}-${index}`,
            sku: String(item?.sku ?? item?.itemMasterSku ?? item?.itemMasterID ?? ''),
            itemName: String(item?.itemName ?? item?.name ?? ''),
            expirationType: item?.expirationType ?? '',
            baseUnitsPerCase: item?.baseUnitsPerCase ?? '',
            expectedProductionDate: formatPurchaseOrderDateForInput(item?.expectedProductionDate) ?? '',
            expectedExpirationDate:
                formatPurchaseOrderDateForInput(item?.expectedExpirationDate ?? item?.expiresOn) ?? '',
            qty: item?.qty != null ? String(item.qty) : '',
            csPallet: item?.csPallet != null ? String(item.csPallet) : '',
            unitPrice,
            vendorSku: item?.vendorSku ?? '',
            year: item?.year != null ? String(item.year) : '',
            purchaseType: item?.purchaseType ?? '',
            week: '',
            suggestedRetailPrice: '',
            unpurchasedOutcome: '',
            sourcingReasons: [],
            countryOfOriginId: '',
        };
    });
}

/**
 * Determines if primary changes require a reason code.
 * Reason code is required when pickupDate, shipperId, or deliveryType change.
 *
 * @param {{initialPrimary: Object, currentPrimary: Object}} params
 * @returns {boolean}
 */
export function getPrimaryRequiresReasonCode({ currentPrimary, initialPrimary }) {
    const primaryDeliveryTypeChanged =
        normalizeToUpperCase(currentPrimary?.deliveryType) !== normalizeToUpperCase(initialPrimary?.deliveryType);
    const primaryPickupDateChanged =
        String(currentPrimary?.pickupDate ?? '') !== String(initialPrimary?.pickupDate ?? '');
    const primaryShipperChanged = String(currentPrimary?.shipperId ?? '') !== String(initialPrimary?.shipperId ?? '');
    return primaryDeliveryTypeChanged || primaryPickupDateChanged || primaryShipperChanged;
}

/**
 * Builds the API payload for updating purchase order primary fields by diffing current vs initial.
 *
 * @param {{initialPrimary: Object, currentPrimary: Object}} params
 * @returns {Object} API payload (only changed fields)
 */
export function buildPurchaseOrderPrimaryUpdatePayload({ currentPrimary, initialPrimary }) {
    const payload = {};

    const fieldKeys = [
        'deliveryType',
        'pickupDate',
        'scheduledDeliveryDate',
        'vendorId',
        'shipperId',
        'crossDockId',
        'crossDockDeliveryDate',
        'facilityId',
        'minTempReq',
        'maxTempReq',
        'specialInstructions',
        'estimatedFreightCost',
    ];
    fieldKeys.forEach((key) => {
        if (
            currentPrimary?.[key] !== undefined &&
            String(currentPrimary?.[key] ?? '') !== String(initialPrimary?.[key] ?? '')
        ) {
            payload[key] = currentPrimary?.[key];
        }
    });

    if (
        Array.isArray(currentPrimary?.buyers) &&
        JSON.stringify(currentPrimary.buyers) !== JSON.stringify(initialPrimary?.buyers)
    ) {
        payload.buyers = currentPrimary.buyers;
    }

    if (currentPrimary?.priority !== initialPrimary?.priority) {
        payload.priority = currentPrimary?.priority;
    }

    const requiresReasonCode = getPrimaryRequiresReasonCode({ initialPrimary, currentPrimary });
    if (requiresReasonCode) {
        payload.reasonCode = currentPrimary?.reasonCode;
    }

    // Map to API field names
    const apiPayload = {};
    if (payload.deliveryType !== undefined) apiPayload.deliveryType = payload.deliveryType;
    if (payload.pickupDate !== undefined) apiPayload.pickupDate = payload.pickupDate;

    if (payload.scheduledDeliveryDate !== undefined) {
        const datetimeString = payload.scheduledDeliveryDate;
        if (datetimeString && String(datetimeString).includes('T')) {
            const [datePart, timePart] = String(datetimeString).split('T');
            apiPayload.scheduledDeliveryDate = datePart;
            apiPayload.deliverTime = timePart || '00:00';
        } else {
            apiPayload.scheduledDeliveryDate = datetimeString;
        }
    }

    if (payload.vendorId !== undefined) apiPayload.vendorID = payload.vendorId;
    if (payload.shipperId !== undefined)
        apiPayload.inboundShipperID = payload.shipperId === '' ? '' : payload.shipperId;
    if (payload.crossDockId !== undefined)
        apiPayload.crossDockID = payload.crossDockId === '' ? '' : payload.crossDockId;
    if (payload.crossDockDeliveryDate !== undefined) apiPayload.crossDockDeliveryDate = payload.crossDockDeliveryDate;
    if (payload.facilityId !== undefined) apiPayload.facilityID = payload.facilityId;
    if (payload.buyers !== undefined) apiPayload.buyers = payload.buyers;
    if (payload.minTempReq !== undefined) apiPayload.minTempReq = payload.minTempReq;
    if (payload.maxTempReq !== undefined) apiPayload.maxTempReq = payload.maxTempReq;
    if (payload.specialInstructions !== undefined) apiPayload.specialInstructions = payload.specialInstructions;
    if (payload.estimatedFreightCost !== undefined) apiPayload.estimatedFreightCost = payload.estimatedFreightCost;
    if (payload.priority !== undefined) apiPayload.priority = payload.priority;
    if (payload.reasonCode !== undefined) apiPayload.reasonCode = payload.reasonCode;

    return apiPayload;
}

/**
 * Normalizes and parses purchase order map item edits into the typed updates object.
 *
 * @param {Object} draft
 * @returns {Object}
 */
export function buildPurchaseOrderMapItemUpdates(draft) {
    const updates = {};

    setDraftStringField({ draft, updates, field: 'expectedExpirationDate' });
    setDraftStringField({ draft, updates, field: 'expectedProductionDate' });
    setDraftIntegerField({ draft, updates, field: 'qty' });
    setDraftStringField({ draft, updates, field: 'vendorSku' });
    setDraftIntegerField({ draft, updates, field: 'csPallet' });
    setDraftFloatField({ draft, updates, field: 'unitPrice' });
    setDraftIntegerField({ draft, updates, field: 'week' });
    setDraftIntegerField({ draft, updates, field: 'year' });
    setDraftStringField({ draft, updates, field: 'purchaseType' });
    setDraftFloatField({ draft, updates, field: 'suggestedRetailPrice' });
    setDraftStringField({ draft, updates, field: 'unpurchasedOutcome' });

    if (Object.prototype.hasOwnProperty.call(draft, 'sourcingReasons')) {
        const values = Array.isArray(draft.sourcingReasons) ? draft.sourcingReasons.filter(Boolean).map(String) : [];
        updates.sourcingReasons = values.slice().sort((a, b) => a.localeCompare(b));
    }

    setDraftIntegerField({ draft, updates, field: 'countryOfOriginId' });

    return updates;
}

/**
 * Normalizes the purchase order map item id used across APIs and UI tables.
 *
 * @param {Object} item
 * @returns {string|null}
 */
export function getPoMapId(item) {
    const raw = item?.poMapID ?? item?.poMapId ?? item?.id ?? null;
    return raw != null ? String(raw) : null;
}

/**
 * Computes a diff of item edits by comparing current UI draft values to the original item values.
 *
 * @param {{item: Object, uiDraft: Object}} params
 * @returns {Object} diff payload (only changed fields, normalized)
 */
export function buildPurchaseOrderMapItemDiff({ item, uiDraft }) {
    const updates = buildPurchaseOrderMapItemUpdates(uiDraft);

    const originalForCompare = buildPurchaseOrderMapItemUpdates({
        expectedExpirationDate: item?.expectedExpirationDate ?? item?.expiresOn ?? '',
        expectedProductionDate: item?.expectedProductionDate ?? '',
        qty: item?.qty ?? '',
        vendorSku: item?.vendorSku ?? '',
        csPallet: item?.csPallet ?? '',
        unitPrice: item?.unitPrice ?? item?.casePrice ?? '',
        week: item?.week ?? item?.shipWeek ?? '',
        year: item?.year ?? item?.shipYear ?? '',
        purchaseType: item?.purchaseType ?? '',
        suggestedRetailPrice: item?.suggestedRetailPrice ?? item?.msrpPrice ?? '',
        unpurchasedOutcome: item?.unpurchasedOutcome ?? item?.whereWouldItGo ?? '',
        sourcingReasons: Array.isArray(item?.sourcingReasons) ? item.sourcingReasons : [],
        countryOfOriginId: item?.countryOfOriginId ?? item?.countryOfOriginID ?? '',
    });

    const diff = {};
    Object.entries(updates).forEach(([key, value]) => {
        if (key === 'sourcingReasons') {
            const a = Array.isArray(value) ? value : [];
            const b = Array.isArray(originalForCompare.sourcingReasons) ? originalForCompare.sourcingReasons : [];
            if (JSON.stringify(a) !== JSON.stringify(b)) diff[key] = a;
            return;
        }
        if (String(value ?? '') !== String(originalForCompare[key] ?? '')) {
            diff[key] = value;
        }
    });

    return diff;
}

export function getItemEditInputValue(itemEditStateRef, poMapId, field) {
    const entry = itemEditStateRef?.current?.inputs?.[poMapId]?.[field];
    if (entry === undefined) return undefined;
    if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
        return entry.value;
    }
    return entry;
}

export function getItemEditSelectValue(itemEditStateRef, poMapId, field) {
    return itemEditStateRef?.current?.selects?.[poMapId]?.[field];
}

export function getItemEditMultiValue(itemEditStateRef, poMapId, field) {
    return itemEditStateRef?.current?.multi?.[poMapId]?.[field];
}

// -----------------------------
// Create + Edit shared helpers
// -----------------------------

export const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

export const toFloatOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export const normalizeItemsForSubmit = ({ itemEditStateRef, items, removedItemIds }) => {
    const removed = removedItemIds instanceof Set ? removedItemIds : new Set();
    const inputsById = itemEditStateRef?.current?.inputs ?? {};
    const selectsById = itemEditStateRef?.current?.selects ?? {};
    const multiById = itemEditStateRef?.current?.multi ?? {};

    return (Array.isArray(items) ? items : [])
        .filter((row) => !removed.has(String(row?.id)))
        .map((row) => {
            const rowId = String(row?.id);
            const inputs = inputsById[rowId] ?? {};
            const selects = selectsById[rowId] ?? {};
            const multi = multiById[rowId] ?? {};

            const merged = {
                ...row,
                ...inputs,
                ...selects,
                ...multi,
            };

            return {
                sku: toIntOrNull(merged.sku) ?? merged.sku,
                itemName: merged.itemName,
                expirationType: merged.expirationType ?? '',
                qty: toIntOrNull(merged.qty),
                vendorSku: merged.vendorSku ?? '',
                csPallet: toIntOrNull(merged.csPallet),
                unitPrice: toFloatOrNull(merged.unitPrice),
                expectedExpirationDate: merged.expectedExpirationDate ?? merged.expiresOn ?? '',
                expectedProductionDate: merged.expectedProductionDate ?? '',
                week: toIntOrNull(merged.week),
                year: toIntOrNull(merged.year),
                purchaseType: merged.purchaseType ?? '',
                suggestedRetailPrice: toFloatOrNull(merged.suggestedRetailPrice),
                unpurchasedOutcome: merged.unpurchasedOutcome ?? '',
                sourcingReasons: Array.isArray(merged.sourcingReasons) ? merged.sourcingReasons : [],
                countryOfOriginId: toIntOrNull(merged.countryOfOriginId),
            };
        });
};

export const validatePurchaseOrderCreate = ({ draft, itemEditStateRef, items, removedItemIds }) => {
    const primaryErrors = [];
    const primaryFieldErrors = {};
    const itemErrors = [];
    const itemSummaryErrors = [];

    const submitItems = normalizeItemsForSubmit({ itemEditStateRef, items, removedItemIds });
    const deliveryDate = draft?.scheduledDeliveryDate ? new Date(`${draft.scheduledDeliveryDate}T00:00:00`) : null;

    if (!submitItems.length) {
        itemSummaryErrors.push('Add at least 1 item to proceed');
    }

    if (!draft?.vendorId) {
        primaryFieldErrors.vendorId = 'Vendor is required';
        primaryErrors.push(primaryFieldErrors.vendorId);
    }
    if (!draft?.deliveryType) {
        primaryFieldErrors.deliveryType = 'Delivery Type is required';
        primaryErrors.push(primaryFieldErrors.deliveryType);
    }
    if (draft?.deliveryType === DELIVERY_TYPE_PICKUP && !draft?.pickUpDate) {
        primaryFieldErrors.pickUpDate = 'Pick Up Date is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.pickUpDate);
    }
    if (draft?.deliveryType === DELIVERY_TYPE_PICKUP && !draft?.shipperId) {
        primaryFieldErrors.shipperId = 'Shipper is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.shipperId);
    }
    if (!draft?.originalRequestedDeliveryDate) {
        primaryFieldErrors.originalRequestedDeliveryDate = 'Original Requested Delivery Date is required';
        primaryErrors.push(primaryFieldErrors.originalRequestedDeliveryDate);
    }
    if (!draft?.scheduledDeliveryDate) {
        primaryFieldErrors.scheduledDeliveryDate = 'Scheduled Delivery Date is required';
        primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
    }
    if (!draft?.scheduledTime) {
        primaryFieldErrors.scheduledTime = 'Scheduled Delivery Time is required';
        primaryErrors.push(primaryFieldErrors.scheduledTime);
    }

    if (deliveryDate) {
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        if (deliveryDate > oneYearFromNow) {
            primaryFieldErrors.scheduledDeliveryDate =
                'Scheduled Delivery Date cannot be more than 1 year in the future';
            primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
        }
    }
    if (!Array.isArray(draft?.facilities) || draft.facilities.length === 0) {
        primaryFieldErrors.facilities = 'Facility is required';
        primaryErrors.push(primaryFieldErrors.facilities);
    }
    if (!Array.isArray(draft?.buyers) || draft.buyers.length === 0) {
        primaryFieldErrors.buyers = 'Buyer(s) are required';
        primaryErrors.push(primaryFieldErrors.buyers);
    }
    if (draft?.crossDockId && !draft?.crossDockDeliveryDate) {
        primaryFieldErrors.crossDockDeliveryDate = 'Cross Dock Delivery Date is required when Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockDeliveryDate);
    }
    if (draft?.crossDockDeliveryDate && !draft?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Cross Dock Delivery Date is provided';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (draft?.isReceivedAtCrossDock === true && !draft?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Received at Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (
        draft?.minTempReq !== '' &&
        draft?.minTempReq != null &&
        draft?.maxTempReq !== '' &&
        draft?.maxTempReq != null
    ) {
        const minTemp = Number(draft.minTempReq);
        const maxTemp = Number(draft.maxTempReq);
        if (Number.isFinite(minTemp) && Number.isFinite(maxTemp) && minTemp > maxTemp) {
            primaryFieldErrors.minTempReq = 'Minimum temperature cannot be greater than maximum temperature';
            primaryErrors.push(primaryFieldErrors.minTempReq);
        }
    }

    const activeItems = Array.isArray(items) ? items.filter((row) => !removedItemIds?.has?.(String(row?.id))) : [];
    submitItems.forEach((item, index) => {
        const sourceItem = activeItems[index];
        const displaySku = item?.sku ?? sourceItem?.sku ?? sourceItem?.itemMasterSku ?? '';
        const displayLabel = displaySku ? `SKU ${displaySku}` : `Row ${index + 1}`;
        const itemMessages = [];

        if (!item?.sku) {
            itemMessages.push(`Item Master SKU is required for ${displayLabel}`);
        }
        if (!item?.qty) {
            itemMessages.push(`Please fill in item quantity for ${displayLabel}`);
        }
        if (!item?.week) {
            itemMessages.push(`Please fill in item week number for ${displayLabel}`);
        }
        if (!item?.year) {
            itemMessages.push(`Please fill in item year for ${displayLabel}`);
        }
        if (!item?.purchaseType) {
            itemMessages.push(`Please select purchase type for ${displayLabel}`);
        }
        const isOpportunistic = String(item?.purchaseType ?? '') === 'OPPORTUNISTIC';
        const expirationType = String(item?.expirationType ?? '');
        const requiresExpirationDate = expirationType === 'EXPIRATION_DATE' && isOpportunistic;
        const requiresProductionDate = expirationType === 'PRODUCTION_DATE' && isOpportunistic;

        if (requiresExpirationDate && !item?.expectedExpirationDate) {
            itemMessages.push(`Please fill in expected expiration date for ${displayLabel}`);
        }

        if (requiresProductionDate && !item?.expectedProductionDate) {
            itemMessages.push(`Please fill in expected production date for ${displayLabel}`);
        }
        if (item?.csPallet !== 0 && !item?.csPallet) {
            itemMessages.push(`Please fill in cases per pallet for ${displayLabel}`);
        }
        if (item?.unitPrice !== 0 && !item?.unitPrice) {
            itemMessages.push(`Please fill in case price for ${displayLabel}`);
        }

        if (itemMessages.length > 0) {
            itemErrors.push({
                id: sourceItem?.id ?? item?.id ?? `row-${index + 1}`,
                sku: displaySku ? String(displaySku) : '',
                messages: itemMessages,
            });
        }
    });

    const summaryErrors = [...primaryErrors, ...itemSummaryErrors, ...itemErrors.flatMap((error) => error.messages)];
    const isValid = summaryErrors.length === 0;

    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        itemErrors,
        itemSummaryErrors,
        summaryErrors,
        submitItems,
    };
};

const deriveEditPrimaryState = ({ primaryDraft, primaryPurchaseOrder }) => {
    const initialPrimary = buildPurchaseOrderPrimaryDraft(primaryPurchaseOrder) ?? {};
    const currentPrimary = primaryDraft ?? initialPrimary;
    const primaryRequiresReasonCode = getPrimaryRequiresReasonCode({ initialPrimary, currentPrimary });
    const scheduledDeliveryRaw = String(currentPrimary?.scheduledDeliveryDate ?? '');
    const deliveryDate = scheduledDeliveryRaw
        ? new Date(scheduledDeliveryRaw.includes('T') ? scheduledDeliveryRaw : `${scheduledDeliveryRaw}T00:00:00`)
        : null;

    return {
        currentPrimary,
        deliveryDate,
        primaryRequiresReasonCode,
        scheduledDeliveryRaw,
    };
};

const validateEditPrimaryFields = ({
    currentPrimary,
    deliveryDate,
    primaryRequiresReasonCode,
    scheduledDeliveryRaw,
}) => {
    const primaryErrors = [];
    const primaryFieldErrors = {};
    const reasonCodeErrors = {};

    if (primaryRequiresReasonCode && !currentPrimary?.reasonCode) {
        reasonCodeErrors.primary = 'Reason code is required when changing pickup date, shipper, or delivery type';
        primaryErrors.push(reasonCodeErrors.primary);
        primaryFieldErrors.reasonCode = reasonCodeErrors.primary;
    }
    if (currentPrimary?.deliveryType === DELIVERY_TYPE_PICKUP && !currentPrimary?.pickupDate) {
        primaryFieldErrors.pickupDate = 'Pick Up Date is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.pickupDate);
    }
    if (currentPrimary?.deliveryType === DELIVERY_TYPE_PICKUP && !currentPrimary?.shipperId) {
        primaryFieldErrors.shipperId = 'Shipper is required when Delivery Type is PICK UP';
        primaryErrors.push(primaryFieldErrors.shipperId);
    }
    if (!Array.isArray(currentPrimary?.buyers) || currentPrimary.buyers.length === 0) {
        primaryFieldErrors.buyers = 'Buyer(s) are required';
        primaryErrors.push(primaryFieldErrors.buyers);
    }
    if (currentPrimary?.crossDockId && !currentPrimary?.crossDockDeliveryDate) {
        primaryFieldErrors.crossDockDeliveryDate = 'Cross Dock Delivery Date is required when Cross Dock is selected';
        primaryErrors.push(primaryFieldErrors.crossDockDeliveryDate);
    }
    if (currentPrimary?.crossDockDeliveryDate && !currentPrimary?.crossDockId) {
        primaryFieldErrors.crossDockId = 'Cross Dock is required when Cross Dock Delivery Date is provided';
        primaryErrors.push(primaryFieldErrors.crossDockId);
    }
    if (scheduledDeliveryRaw && deliveryDate && Number.isNaN(deliveryDate.getTime())) {
        primaryFieldErrors.scheduledDeliveryDate = 'Scheduled Delivery Date must be valid';
        primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
    }
    if (deliveryDate) {
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        if (deliveryDate > oneYearFromNow) {
            primaryFieldErrors.scheduledDeliveryDate =
                'Scheduled Delivery Date cannot be more than 1 year in the future';
            primaryErrors.push(primaryFieldErrors.scheduledDeliveryDate);
        }
    }
    if (
        currentPrimary?.minTempReq !== '' &&
        currentPrimary?.minTempReq != null &&
        currentPrimary?.maxTempReq !== '' &&
        currentPrimary?.maxTempReq != null
    ) {
        const minTemp = Number(currentPrimary.minTempReq);
        const maxTemp = Number(currentPrimary.maxTempReq);
        if (Number.isFinite(minTemp) && Number.isFinite(maxTemp) && minTemp > maxTemp) {
            primaryFieldErrors.minTempReq = 'Minimum temperature cannot be greater than maximum temperature';
            primaryErrors.push(primaryFieldErrors.minTempReq);
        }
    }

    return {
        primaryErrors,
        primaryFieldErrors,
        reasonCodeErrors,
    };
};

const buildEditItemChangeSet = ({ addedItems, itemEditStateRef, purchaseOrderDetail, removedItemIds }) => {
    const addedItemIds = new Set((addedItems ?? []).map((item) => String(item?.id)));
    const newItemsToAdd = (addedItems ?? []).filter((item) => !removedItemIds?.has?.(String(item?.id)));
    const hasAdditions = newItemsToAdd.length > 0;

    const removalIds = Array.from(removedItemIds ?? []).filter((id) => !addedItemIds.has(String(id)));
    const hasRemovals = removalIds.length > 0;

    const items = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
    const activeItemCount =
        (Array.isArray(items) ? items : [])
            .map((item) => getPoMapId(item))
            .filter((id) => id && !removedItemIds?.has?.(id)).length +
        (Array.isArray(addedItems) ? addedItems : []).filter((item) => !removedItemIds?.has?.(String(item?.id))).length;

    const itemUpdateEntries = (Array.isArray(items) ? items : [])
        .map((item) => {
            const poMapId = getPoMapId(item);
            if (!poMapId) return null;
            if (removedItemIds?.has?.(poMapId)) return null;

            const draft = {};

            const itemEditInputFields = [
                'expectedExpirationDate',
                'expectedProductionDate',
                'qty',
                'vendorSku',
                'csPallet',
                'unitPrice',
                'week',
                'year',
                'suggestedRetailPrice',
            ];

            const itemEditSelectFields = ['purchaseType', 'unpurchasedOutcome', 'countryOfOriginId'];

            itemEditInputFields.forEach((field) => {
                const value = getItemEditInputValue(itemEditStateRef, poMapId, field);
                if (value !== undefined) {
                    draft[field] = value;
                }
            });

            itemEditSelectFields.forEach((field) => {
                const value = getItemEditSelectValue(itemEditStateRef, poMapId, field);
                if (value !== undefined) {
                    draft[field] = value;
                }
            });

            const sourcing = getItemEditMultiValue(itemEditStateRef, poMapId, 'sourcingReasons');
            if (sourcing !== undefined) {
                draft.sourcingReasons = sourcing;
            }

            const diff = buildPurchaseOrderMapItemDiff({ item, uiDraft: draft });
            if (Object.keys(diff).length === 0) return null;
            return [poMapId, diff];
        })
        .filter(Boolean);

    const hasQtyUpdates = itemUpdateEntries.some(([, updates]) => Object.prototype.hasOwnProperty.call(updates, 'qty'));

    return {
        items,
        newItemsToAdd,
        hasAdditions,
        removalIds,
        hasRemovals,
        activeItemCount,
        itemUpdateEntries,
        hasQtyUpdates,
    };
};

const getEditReasonCodeMessages = ({
    addItemReasonCode,
    hasAdditions,
    hasQtyUpdates,
    hasRemovals,
    qtyReasonCode,
    removeReasonCode,
}) => {
    const reasonCodeErrors = {};
    const addMessage = hasAdditions && !addItemReasonCode ? 'Reason code is required when adding items' : null;
    const removeMessage =
        hasRemovals && !removeReasonCode ? 'Remove reason code is required when removing items' : null;
    const qtyMessage =
        hasQtyUpdates && !qtyReasonCode ? 'Qty reason code is required when changing case quantity' : null;

    if (addMessage) reasonCodeErrors.add = addMessage;
    if (removeMessage) reasonCodeErrors.remove = removeMessage;
    if (qtyMessage) reasonCodeErrors.qty = qtyMessage;

    return {
        addMessage,
        qtyMessage,
        reasonCodeErrors,
        removeMessage,
    };
};

const validateEditAddedItems = ({ itemEditStateRef, newItemsToAdd }) => {
    const itemErrors = [];

    newItemsToAdd.forEach((item, index) => {
        const rowId = String(item?.id ?? '');
        const displaySku = item?.sku ?? '';
        const displayLabel = displaySku ? `SKU ${displaySku}` : `Row ${index + 1}`;
        const itemMessages = [];

        const qtyInput = getItemEditInputValue(itemEditStateRef, rowId, 'qty');
        const unitPriceInput = getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice');
        const csPalletInput = getItemEditInputValue(itemEditStateRef, rowId, 'csPallet');
        const weekInput = getItemEditInputValue(itemEditStateRef, rowId, 'week');
        const yearInput = getItemEditInputValue(itemEditStateRef, rowId, 'year');
        const purchaseTypeInput = getItemEditSelectValue(itemEditStateRef, rowId, 'purchaseType');
        const expectedExpirationDateInput = getItemEditInputValue(itemEditStateRef, rowId, 'expectedExpirationDate');
        const expectedProductionDateInput = getItemEditInputValue(itemEditStateRef, rowId, 'expectedProductionDate');

        const qty = qtyInput === undefined ? item?.qty : qtyInput;
        const unitPrice = unitPriceInput === undefined ? item?.unitPrice : unitPriceInput;
        const csPallet = csPalletInput === undefined ? item?.csPallet : csPalletInput;
        const week = weekInput === undefined ? item?.week : weekInput;
        const year = yearInput === undefined ? item?.year : yearInput;
        const purchaseType = purchaseTypeInput === undefined ? item?.purchaseType : purchaseTypeInput;
        const expectedExpirationDate =
            expectedExpirationDateInput === undefined ? item?.expectedExpirationDate : expectedExpirationDateInput;
        const expectedProductionDate =
            expectedProductionDateInput === undefined ? item?.expectedProductionDate : expectedProductionDateInput;
        const expirationType = String(item?.expirationType ?? '');

        if (!displaySku) {
            itemMessages.push(`Item Master SKU is required for ${displayLabel}`);
        }
        if (!purchaseType) {
            itemMessages.push(`Please select purchase type for ${displayLabel}`);
        }
        const isOpportunistic = String(purchaseType ?? '') === 'OPPORTUNISTIC';
        const requiresExpirationDate = expirationType === 'EXPIRATION_DATE' && isOpportunistic;
        const requiresProductionDate = expirationType === 'PRODUCTION_DATE' && isOpportunistic;

        if (requiresExpirationDate && !expectedExpirationDate) {
            itemMessages.push(`Please fill in expected expiration date for ${displayLabel}`);
        }

        if (requiresProductionDate && !expectedProductionDate) {
            itemMessages.push(`Please fill in expected production date for ${displayLabel}`);
        }
        if (!qty) {
            itemMessages.push(`Please fill in item quantity for ${displayLabel}`);
        }
        if (csPallet !== 0 && !csPallet) {
            itemMessages.push(`Please fill in cases per pallet for ${displayLabel}`);
        }
        if (unitPrice !== 0 && !unitPrice) {
            itemMessages.push(`Please fill in case price for ${displayLabel}`);
        }
        if (!week) {
            itemMessages.push(`Please fill in item week number for ${displayLabel}`);
        }
        if (!year) {
            itemMessages.push(`Please fill in item year for ${displayLabel}`);
        }

        if (itemMessages.length > 0) {
            itemErrors.push({
                id: rowId || `row-${index + 1}`,
                sku: displaySku ? String(displaySku) : '',
                messages: itemMessages,
            });
        }
    });

    return itemErrors;
};

const validateEditUpdatedItems = ({ itemUpdateEntries }) => {
    const itemErrors = [];

    itemUpdateEntries.forEach(([poMapId, updates]) => {
        if (Object.prototype.hasOwnProperty.call(updates, 'purchaseType') && !updates.purchaseType) {
            itemErrors.push({
                id: poMapId,
                sku: String(poMapId),
                messages: ['Please select purchase type for this item'],
            });
        }
    });

    return itemErrors;
};

export const validatePurchaseOrderEdit = ({
    addedItems,
    addItemReasonCode,
    itemEditStateRef,
    primaryDraft,
    primaryPurchaseOrder,
    purchaseOrderDetail,
    qtyReasonCode,
    removedItemIds,
    removeReasonCode,
}) => {
    const itemSummaryErrors = [];

    const { currentPrimary, deliveryDate, primaryRequiresReasonCode, scheduledDeliveryRaw } = deriveEditPrimaryState({
        primaryDraft,
        primaryPurchaseOrder,
    });

    const {
        primaryErrors,
        primaryFieldErrors,
        reasonCodeErrors: primaryReasonCodeErrors,
    } = validateEditPrimaryFields({
        currentPrimary,
        deliveryDate,
        primaryRequiresReasonCode,
        scheduledDeliveryRaw,
    });

    const { activeItemCount, hasAdditions, hasQtyUpdates, hasRemovals, itemUpdateEntries, newItemsToAdd, removalIds } =
        buildEditItemChangeSet({
            addedItems,
            itemEditStateRef,
            purchaseOrderDetail,
            removedItemIds,
        });

    const {
        addMessage,
        qtyMessage,
        reasonCodeErrors: itemReasonCodeErrors,
        removeMessage,
    } = getEditReasonCodeMessages({
        addItemReasonCode,
        hasAdditions,
        hasQtyUpdates,
        hasRemovals,
        qtyReasonCode,
        removeReasonCode,
    });

    if (addMessage) {
        itemSummaryErrors.push(addMessage);
    }
    if (removeMessage) {
        itemSummaryErrors.push(removeMessage);
    }
    if (activeItemCount === 0) {
        itemSummaryErrors.push('At least one item is required');
    }
    if (qtyMessage) {
        itemSummaryErrors.push(qtyMessage);
    }

    const itemErrors = [
        ...validateEditAddedItems({ itemEditStateRef, newItemsToAdd }),
        ...validateEditUpdatedItems({ itemUpdateEntries }),
    ];

    const reasonCodeErrors = {
        ...primaryReasonCodeErrors,
        ...itemReasonCodeErrors,
    };

    const summaryErrors = [...primaryErrors, ...itemSummaryErrors, ...itemErrors.flatMap((error) => error.messages)];
    const isValid = summaryErrors.length === 0;

    return {
        isValid,
        primaryErrors,
        primaryFieldErrors,
        itemErrors,
        itemSummaryErrors,
        reasonCodeErrors,
        itemUpdateEntries,
        newItemsToAdd,
        removalIds,
        hasAdditions,
        hasRemovals,
        hasQtyUpdates,
        primaryRequiresReasonCode,
        summaryErrors,
    };
};
